import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { getUserData, hasConsentForCookies, saveSearchData } from '../utils/cookieUtils';

interface SearchFormProps {
    onSearch: (
        postcode: string, 
        radius: number, 
        residentialCare: boolean, 
        dementiaCare: boolean, 
        nursingCare: boolean, 
        sort: 'score' | 'fee_low' | 'fee_high' | 'event_date',
        fundingType: 'all' | 'self_funding' | 'la_funding' | 'la+topup_funding' | 'chc_funding',
        eventFilter: string
    ) => void;
    loading?: boolean;
    hasResults?: boolean;
    isCollapsed?: boolean;
    onToggleCollapse?: () => void;
    resultsCount?: number;
    searchPerformed?: boolean;
    /** Externally-set postcode (e.g. from ?carehome= URL param). Overrides internal state when changed. */
    externalPostcode?: string;
    /** Error message from the most recent search (e.g. location not found / outside UK). */
    errorMessage?: string | null;
}

const SearchForm: React.FC<SearchFormProps> = ({ 
    onSearch, 
    loading = false, 
    hasResults = false,
    isCollapsed = false,
    onToggleCollapse,
    resultsCount = 0,
    searchPerformed = false,
    externalPostcode,
    errorMessage = null
}) => {
    const [postcode, setPostcode] = useState('');
    const [radius, setRadius] = useState(10);
    const [isFocused, setIsFocused] = useState(false);
    const [residentialCare, setResidentialCare] = useState(false);
    const [dementiaCare, setDementiaCare] = useState(false);
    const [nursingCare, setNursingCare] = useState(false);
    const [sort, setSort] = useState<'score' | 'fee_low' | 'fee_high' | 'event_date'>('score');
    // Funding type - default to "all"
    const [fundingType, setFundingType] = useState<'all' | 'self_funding' | 'la_funding' | 'la+topup_funding' | 'chc_funding'>('all');
    // Event filter (update 2026-03-30.2)
    const [eventFilter, setEventFilter] = useState<string>('-1');
    const [eventNames, setEventNames] = useState<string[]>([]);
    // Result message
    const [resultMessage, setResultMessage] = useState<string>('');

    // Fetch event list on mount (update 2026-03-30.2)
    useEffect(() => {
        api.getEventList().then(data => {
            setEventNames(data.events || []);
        });
    }, []);

    // Apply externally-set postcode (update 2026-03-01.2)
    useEffect(() => {
        if (externalPostcode !== undefined) {
            setPostcode(externalPostcode);
        }
    }, [externalPostcode]);

    // Initialize form with saved search data if cookies are accepted
    useEffect(() => {
        if (hasConsentForCookies()) {
            const userData = getUserData();
            if (userData?.lastSearch && !postcode.trim()) {
                // Only pre-fill if postcode is empty
                if (userData.lastSearch.starting_post_code) {
                    setPostcode(userData.lastSearch.starting_post_code);
                }
                if (userData.lastSearch.miles_radius) {
                    setRadius(userData.lastSearch.miles_radius);
                }
                if (userData.lastSearch.residential_needs !== undefined) {
                    setResidentialCare(userData.lastSearch.residential_needs);
                }
                if (userData.lastSearch.dementia_needs !== undefined) {
                    setDementiaCare(userData.lastSearch.dementia_needs);
                }
                if (userData.lastSearch.nursing_needs !== undefined) {
                    setNursingCare(userData.lastSearch.nursing_needs);
                }
                if (userData.lastSearch.sort) {
                    setSort(userData.lastSearch.sort);
                }
                // Load funding type
                if (userData.lastSearch.fundingType) {
                    setFundingType(userData.lastSearch.fundingType);
                }
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Auto-collapse when results are found
    useEffect(() => {
        if (hasResults && !isFocused && postcode.trim()) {
            // Auto-collapse logic will be handled by parent component
        }
    }, [hasResults, isFocused, postcode]);

    // When an event filter is selected, force sort to 'event_date' and disable the
    // dropdown (update 2026_04_22.2). When the user switches back to "All care homes"
    // (eventFilter === '-1'), revert to 'score'. Fee-based sorts are also hidden while
    // the event filter is active (update 2026_04_22.3).
    useEffect(() => {
        if (eventFilter !== '-1') {
            if (sort !== 'event_date') setSort('event_date');
        } else if (sort === 'event_date') {
            setSort('score');
        }
    }, [eventFilter, sort]);

    // Update result message based on search results / error state
    useEffect(() => {
        if (errorMessage) {
            setResultMessage(errorMessage);
        } else if (resultsCount === 0 && searchPerformed) {
            setResultMessage('No Care Homes found, try broadening search filters.');
        } else if (resultsCount >= 50) {
            setResultMessage('Top 50 shown, refine filter for more');
        } else if (resultsCount > 0) {
            setResultMessage('');
        }
    }, [resultsCount, searchPerformed, errorMessage]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (postcode.trim()) {
            // Save search data to cookies if consent is given
            if (hasConsentForCookies()) {
                saveSearchData({
                    starting_post_code: postcode.trim(),
                    miles_radius: radius,
                    residential_needs: residentialCare,
                    dementia_needs: dementiaCare,
                    nursing_needs: nursingCare,
                    sort: sort,
                    fundingType: fundingType
                });
            }
            
            onSearch(postcode.trim(), radius, residentialCare, dementiaCare, nursingCare, sort, fundingType, eventFilter);
        }
    };

    const handleFocus = () => {
        setIsFocused(true);
        // Clear result message when postcode is selected
        setResultMessage('');
        // Expand search form if it's currently collapsed
        if (isCollapsed && onToggleCollapse) {
            onToggleCollapse();
        }
    };

    const handleBlur = () => {
        setIsFocused(false);
    };

    // Expand/Collapse Icon
    const ExpandIcon = () => (
        <div
            className="expand-collapse-arrow"
            onClick={onToggleCollapse}
            title={isCollapsed ? "Expand search options" : "Collapse search options"}
            aria-label={isCollapsed ? "Expand search form" : "Collapse search form"}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onToggleCollapse?.();
                }
            }}
        >
            <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="currentColor"
                className={`expand-icon ${isCollapsed ? 'collapsed' : 'expanded'}`}
            >
                {isCollapsed ? (
                    // Down arrow when collapsed
                    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                ) : (
                    // Up arrow when expanded
                    <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/>
                )}
            </svg>
        </div>
    );

    return (
        <div className={`search-form ${isCollapsed ? 'collapsed' : 'expanded'}`}>
            <form onSubmit={handleSubmit}>
                {/* Always visible: Postcode input with expand/collapse icon */}
                <div className="postcode-group">
                    {!isCollapsed && <label htmlFor="postcode">Search by postcode or place</label>}
                    <div className="postcode-input-container">
                        <input
                            id="postcode"
                            type="text"
                            value={postcode}
                            onChange={(e) => setPostcode(e.target.value)}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            placeholder={isCollapsed ? "Postcode or place" : "Enter UK postcode or place (e.g. Liverpool)"}
                            title="Enter a UK postcode (e.g. 'SW1A' or 'SW1A 1AA') or a place name (e.g. Liverpool, Glasgow)"
                            minLength={2}
                            required
                            disabled={loading}
                            className={isCollapsed ? 'collapsed-input' : 'expanded-input'}
                            style={{ width: isCollapsed ? '15ch' : '100%' }}
                        />
                        <ExpandIcon />
                    </div>
                    {/* Result / error message div - always visible, below the entry box */}
                    <div className="search-result-message" style={{ 
                        minHeight: '1.2rem',
                        width: '100%', 
                        fontSize: '12px',
                        lineHeight: 1.3,
                        background: 'rgba(255, 255, 255, 0.95)',
                        padding: '0.25rem 0.5rem',
                        borderBottomLeftRadius: '8px',
                        borderBottomRightRadius: '8px',
                        color: 'red',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word'
                    }}>
                        {resultMessage}
                    </div>
                </div>

                {/* Conditionally visible: Radius and Submit button */}
                {!isCollapsed && (
                    <>
                        <div className="form-group">
                            <label htmlFor="radius">
                                Search Radius: {radius} mile{radius !== 1 ? 's' : ''}
                            </label>
                            <input
                                id="radius"
                                type="range"
                                min="1"
                                max="50"
                                value={radius}
                                onChange={(e) => setRadius(parseInt(e.target.value))}
                                disabled={loading}
                            />
                            <div className="radius-labels">
                                <span>1 mile</span>
                                <span>50 miles</span>
                            </div>
                        </div>

                        {/* Event filter (update 2026-03-30.2) */}
                        {eventNames.length > 0 && (
                            <div className="form-group">
                                <label htmlFor="eventFilter">Upcoming events</label>
                                <select
                                    id="eventFilter"
                                    value={eventFilter}
                                    onChange={(e) => setEventFilter(e.target.value)}
                                    disabled={loading}
                                >
                                    <option value="-1">All care homes</option>
                                    <option value="-2">Any event</option>
                                    {eventNames.map(name => (
                                        <option key={name} value={name}>{name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Filter Options */}
                        <div className="form-group filters-group">
                            <fieldset>
                                <legend>Looking for a care home that supports</legend>
                                
                                <div className="pill-buttons-group">
                                    <button
                                        type="button"
                                        className={`pill-button ${residentialCare ? 'enabled' : ''}`}
                                        onClick={() => setResidentialCare(!residentialCare)}
                                        disabled={loading}
                                        aria-pressed={residentialCare}
                                    >
                                        Residential
                                    </button>

                                    <button
                                        type="button"
                                        className={`pill-button ${dementiaCare ? 'enabled' : ''}`}
                                        onClick={() => setDementiaCare(!dementiaCare)}
                                        disabled={loading}
                                        aria-pressed={dementiaCare}
                                    >
                                        Dementia
                                    </button>

                                    <button
                                        type="button"
                                        className={`pill-button ${nursingCare ? 'enabled' : ''}`}
                                        onClick={() => setNursingCare(!nursingCare)}
                                        disabled={loading}
                                        aria-pressed={nursingCare}
                                    >
                                        Nursing
                                    </button>
                                </div>
                            </fieldset>
                        </div>

                        {/* Funding Options */}
                        <div className="form-group">
                            <label htmlFor="fundingType">We will be funding our care by:</label>
                            <select
                                id="fundingType"
                                value={fundingType}
                                onChange={(e) => setFundingType(e.target.value as 'all' | 'self_funding' | 'la_funding' | 'la+topup_funding' | 'chc_funding')}
                                disabled={loading}
                            >
                                <option value="all">All types of funding</option>
                                <option value="self_funding">Self funding</option>
                                <option value="la_funding">Local authority funding</option>
                                <option value="la+topup_funding">Local authority with top-up</option>
                                <option value="chc_funding">NHS funding (CHC)</option>
                            </select>
                        </div>

                        {/* Sort Preference */}
                        <div className="form-group">
                            <label htmlFor="sort">Sort by</label>
                            <select
                                id="sort"
                                value={sort}
                                onChange={(e) => setSort(e.target.value as 'score' | 'fee_low' | 'fee_high' | 'event_date')}
                                // When an event filter is active, force event_date sort and
                                // disable the control (update 2026_04_22.2).
                                disabled={loading || eventFilter !== '-1'}
                            >
                                {eventFilter !== '-1' ? (
                                    <option value="event_date">Event date</option>
                                ) : (
                                    <>
                                        <option value="score">Score</option>
                                        <option value="fee_low">Fee: Low to High</option>
                                        <option value="fee_high">Fee: High to Low</option>
                                    </>
                                )}
                            </select>
                        </div>

                        <button 
                            type="submit" 
                            disabled={loading || !postcode.trim()}
                            style={{ width: '100%', background: '#ED530F', borderColor: '#ED530F' }}
                        >
                            {loading ? 'Searching...' : 'Find Care Homes'}
                        </button>

                        {/* Copyright text */}
                        <div style={{ 
                            marginTop: '1rem', 
                            textAlign: 'center', 
                            fontSize: '11px', 
                            color: '#025150' 
                        }}>
                            © 2025 Autumna. All rights reserved.
                        </div>
                    </>
                )}
            </form>
        </div>
    );
};

export default SearchForm; 
